import { useState } from 'react'
import translations from './translations'
import { FormattedMessage } from 'components/Commons/Translation'
import useUploadDrop from 'hooks/upload/useUploadDrop'
import { LoadingDots } from '@nfinite/react-hubstairs/components'
import IconLabel from 'components/Commons/Icon/IconLabel'
import { usePaginatedList } from 'components/Commons/PaginatedList'

import {
  TextInput,
  Clear,
  PictureIcon,
  PictureWrapper,
  UploadTooltip,
  UploadTitle,
  UploadZone,
  CloseTooltipIcon,
  Line,
  MiddleText,
  DropZoneContainer,
  InputZoneContent,
  Button,
  InputContainer,
  TooltipContainer,
  Container,
  UploadIcon,
  BrowseButton,
  InfoTitle,
  ImageDropZone,
  ErrorInfo,
  LoadingDotsWrapper,
  LineWrapper,
  PictureSearchTooltip,
  DropZoneContent,
  DragActiveMessage,
} from './styled'

import Cross from 'assets/svg/close.svg?react'
import CrossSvg from 'assets/svg/Cross.svg?react'
import PlusSvg from 'assets/svg/plus.svg?react'
import ImageSvg from './assets/preview.svg?react'
import UploadSvg from 'assets/svg/upload.svg?react'
import PictureSearchSvg from './assets/picture_search.svg?react'
import ErrorSvg from './assets/exclamationNoBack.svg?react'
import useFileUpload from './hooks/useFileUpload'
import { FILTERS } from './SearchBar'

export const SORT_BY_RELEVANCY = { relevancy: 'DESC' }
export const MAX_IMAGE_SIZE = 15000000

const MIN_URL_LENGTH = 8

function isValidUrl(url) {
  const lowerCaseUrl = url.toLowerCase()
  return lowerCaseUrl.length > MIN_URL_LENGTH && lowerCaseUrl.startsWith('https://')
}

export function usePictureSearchInterface() {
  const { setFiltersAndSort } = usePaginatedList()
  const [isOpen, setIsOpen] = useState(false)
  const [pasteValue, setPasteValue] = useState('')
  const [error, setError] = useState(false)

  function onCloseTooltip() {
    setIsOpen(false)
    setPasteValue('')
    setError(false)
  }

  function onPasteUrlSearch() {
    const isValid = isValidUrl(pasteValue)

    if (!isValid) {
      setError(translations.errorInvalidUrl)
      return
    }
    setFiltersAndSort({
      filters: {
        [FILTERS.IMAGE]: pasteValue,
      },
      sort: SORT_BY_RELEVANCY,
    })
    onCloseTooltip()
  }

  const controls = {
    tooltip: { isOpen, setIsOpen, close: onCloseTooltip },
    error: {
      value: error,
      set: setError,
    },
    pasteUrl: { value: pasteValue, set: setPasteValue, onSearch: onPasteUrlSearch },
  }

  return controls
}

function UploadTooltipContent({ controls }) {
  return (
    <TooltipContainer>
      <UploadTitle>
        <FormattedMessage {...translations.uploadTitle} />
        <CloseTooltipIcon onClick={controls.tooltip.close} component={CrossSvg} />
      </UploadTitle>
      <DropZone controls={controls} />
    </TooltipContainer>
  )
}

function DropZone({ controls }) {
  return <DropZoneFileUpload controls={controls} onComplete={controls.tooltip.close} onError={controls.error.set} />
}

function DropZoneError({ error }) {
  return error ? (
    <ErrorInfo>
      <ErrorInfo.Container>
        <ErrorInfo.Icon component={ErrorSvg} />
        <ErrorInfo.Text>
          <FormattedMessage {...error} />
        </ErrorInfo.Text>
      </ErrorInfo.Container>
    </ErrorInfo>
  ) : null
}

function PasteUrlZone({ controlsPasteUrl }) {
  function onKeyDown(event) {
    if ((event.code === 'Enter' || event.key === 'Enter') && controlsPasteUrl.value) {
      event.preventDefault()
      controlsPasteUrl.onSearch()
    }
  }

  return (
    <InputContainer>
      <InputZoneContent>
        <TextInput
          dataTestId="input_search"
          name="hb-search"
          value={controlsPasteUrl.value}
          onChange={e => controlsPasteUrl.set(e.target.value)}
          placeholder={translations.copyLinkPlaceholder}
          right={
            controlsPasteUrl.value.length >= 1 && (
              <Clear dataTestId="cross_clear" onClick={() => controlsPasteUrl.set('')} component={Cross} />
            )
          }
          autoComplete="off"
          onKeyDown={onKeyDown}
        />
        <Button variant="outline" color="primary" onClick={controlsPasteUrl.onSearch}>
          <FormattedMessage {...translations.btnSearchLabel} />
        </Button>
      </InputZoneContent>
    </InputContainer>
  )
}

function HorizontalSeparator() {
  return (
    <LineWrapper>
      <Line>
        <MiddleText>
          <FormattedMessage {...translations.orLabel} />
        </MiddleText>
      </Line>
    </LineWrapper>
  )
}

function DropZoneFileUpload({ controls, onComplete, onError }) {
  const { acceptedFileTypes, onUploadComplete, onUploadFailed, customFileValidator, onUploadRejected } = useFileUpload({
    onComplete,
    onError,
  })

  const {
    getRootProps,
    getInputProps,
    loading,
    isDragActive,
    open: openDropZone,
  } = useUploadDrop({
    accept: acceptedFileTypes,
    onUploadComplete,
    onUploadFailed,
    onUploadRejected,
    customFileValidator,
    maxSize: MAX_IMAGE_SIZE,
    signed: true,
    notifyOnReject: false,
    noClick: true,
  })

  return (
    <Container {...getRootProps()}>
      <UploadZone isDragActive={isDragActive} errorBorder={!!controls.error.value}>
        {isDragActive ? (
          <DragActiveMessage>
            <FormattedMessage {...translations.dropImageHere} />
          </DragActiveMessage>
        ) : (
          <DropZoneContainer>
            {loading ? (
              <LoadingDotsWrapper>
                <LoadingDots />
              </LoadingDotsWrapper>
            ) : (
              <>
                <DropZoneError error={controls.error.value} />
                <ImageDropZone>
                  <InfoTitle>
                    <InfoTitle.Icon component={ImageSvg} />
                    <FormattedMessage {...translations.addAnImage} />
                  </InfoTitle>

                  <DropZoneContent>
                    <IconLabel
                      Icon={<UploadIcon component={UploadSvg} />}
                      label={<FormattedMessage {...translations.dragDropImage} />}
                      color="gray,second"
                    />
                    <BrowseButton color="primary" size="small" onClick={openDropZone}>
                      <BrowseButton.Content>
                        <BrowseButton.Label>
                          <FormattedMessage {...translations.browse} />
                        </BrowseButton.Label>
                        <BrowseButton.Icon component={PlusSvg} />
                      </BrowseButton.Content>
                    </BrowseButton>
                  </DropZoneContent>
                </ImageDropZone>
              </>
            )}
            <HorizontalSeparator />
            <PasteUrlZone controlsPasteUrl={controls.pasteUrl} />
          </DropZoneContainer>
        )}
      </UploadZone>
      <input id={'image_search_input'} {...getInputProps()} />
    </Container>
  )
}

function SearchByPicture({ controls, setHelpTooltipOpen }) {
  return (
    <UploadTooltip
      isOpen={controls.tooltip.isOpen}
      arrowSize={12}
      distance={12}
      direction={'down'}
      arrow
      content={<UploadTooltipContent controls={controls} />}
      onClickOutside={controls.tooltip.close}
      zIndex={9993}
    >
      <PictureWrapper
        onMouseEnter={() => setHelpTooltipOpen(true)}
        onMouseLeave={() => setHelpTooltipOpen(false)}
        onClick={() => controls.tooltip.setIsOpen(true)}
      >
        <PictureIcon component={PictureSearchSvg} />
      </PictureWrapper>
    </UploadTooltip>
  )
}

function PictureSearch({ controls }) {
  // Implemented a small hover tooltip.
  // Opted for onMouseEnter/onMouseLeave instead of useHover due to conflicts with two tooltips on the same component.
  const [isHelpTooltipOpen, setHelpTooltipOpen] = useState(false)

  return (
    <PictureSearchTooltip
      isOpen={isHelpTooltipOpen}
      arrowSize={12}
      distance={20}
      direction={'down'}
      content={
        <PictureSearchTooltip.Content>
          <FormattedMessage {...translations.searchByImagesTooltip} />
        </PictureSearchTooltip.Content>
      }
    >
      <SearchByPicture setHelpTooltipOpen={setHelpTooltipOpen} controls={controls} />
    </PictureSearchTooltip>
  )
}

export default PictureSearch
